import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import DeleteIcon from '@mui/icons-material/Delete'
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { CollapsibleBoxInternalState } from '@ui/structure'
import { useControlStore, useControlStoreApi, useElementSelectionStore } from '@editorStores'
import { useModelClickListeners, useResultsInvalidation, useSelectionMode } from '@editorHooks'
import { getModel } from '@queries'
import { alignRoofSlabs, type RoofSlabAlignmentConfigPayload } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'

const ALIGN_REFERENCE_SELECTION_KEY = 'roof-align-reference'

const DEFAULT_ALIGNMENT_CONFIG: RoofSlabAlignmentConfigPayload = {
  polygonExtensionSlabsToAlign: 0.0,
  polygonExtensionSlabsToAlignAgainst: 0.2,
}

interface Props {
  selectedElement: string
  onAligned?: () => void
}

const RoofSlabAlignmentForm = ({ selectedElement, onAligned }: Props): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const invalidateResults = useResultsInvalidation()
  const { setSelectionMode, selectionModeKey } = useSelectionMode()
  const controlStoreApi = useControlStoreApi()
  const setHighlightedIds = useElementSelectionStore(state => state.setHighlightedIds)
  const resetHighlightedIds = useElementSelectionStore(state => state.resetHighlightedIds)
  const problemViewActiveErrors = useControlStore(state => state.problemViewActiveErrors)
  const problemViewActiveWarnings = useControlStore(state => state.problemViewActiveWarnings)
  const setProblemViewActiveErrors = useControlStore(state => state.setProblemViewActiveErrors)
  const setProblemViewActiveWarnings = useControlStore(state => state.setProblemViewActiveWarnings)

  const previousProblemViewRef = useRef({ errors: true, warnings: true })

  const [alignAgainstGuids, setAlignAgainstGuids] = useState<Set<string>>(new Set())
  const [alignmentConfig, setAlignmentConfig] =
    useState<RoofSlabAlignmentConfigPayload>(DEFAULT_ALIGNMENT_CONFIG)

  const isAlignReferenceMode = selectionModeKey === ALIGN_REFERENCE_SELECTION_KEY

  const toggleAlignAgainst = useCallback(
    (event: { object: { name: string; userData?: { elementType?: string } } }) => {
      const guid = event.object.name
      const elementType = event.object.userData?.elementType
      if (elementType !== 'roof_slabs' || guid === selectedElement) return
      setAlignAgainstGuids(prev => {
        const next = new Set(prev)
        if (next.has(guid)) next.delete(guid)
        else next.add(guid)
        return next
      })
    },
    [selectedElement],
  )

  useModelClickListeners(
    toggleAlignAgainst,
    [selectedElement, toggleAlignAgainst],
    isAlignReferenceMode,
  )

  useEffect(() => {
    setHighlightedIds(Array.from(alignAgainstGuids))
    return () => resetHighlightedIds()
  }, [alignAgainstGuids, setHighlightedIds, resetHighlightedIds])

  // The form unmounts when the roof slab is deselected. If our reference-picking mode is still
  // active at that point, its snackbar disappears with the form while the store would stay in
  // selection mode, invisibly blocking all element selection — so end the mode on unmount.
  useEffect(() => {
    return () => {
      const state = controlStoreApi.getState()
      if (state.selectionModeKey === ALIGN_REFERENCE_SELECTION_KEY) {
        state.toggleSelectionMode()
        state.selectionAbortCallback?.()
      }
    }
  }, [controlStoreApi])

  const onStartSelectingReference = useCallback(() => {
    previousProblemViewRef.current = {
      errors: problemViewActiveErrors,
      warnings: problemViewActiveWarnings,
    }
    setProblemViewActiveErrors(false)
    setProblemViewActiveWarnings(false)
    setSelectionMode({
      key: ALIGN_REFERENCE_SELECTION_KEY,
      message:
        'Klicken Sie im Modell auf Dachflächen, die als Referenz zum Zuschneiden dienen sollen. Schließen Sie diese Meldung zum Beenden.',
      onAbort: () => {
        setProblemViewActiveErrors(previousProblemViewRef.current.errors)
        setProblemViewActiveWarnings(previousProblemViewRef.current.warnings)
      },
    })
  }, [
    problemViewActiveErrors,
    problemViewActiveWarnings,
    setProblemViewActiveErrors,
    setProblemViewActiveWarnings,
    setSelectionMode,
  ])

  const { mutate: submitAlignment, isLoading } = useMutation(
    () =>
      alignRoofSlabs.request(projectId as string, {
        roofSlabGuidsToAlign: [selectedElement],
        roofSlabGuidsToAlignAgainst: Array.from(alignAgainstGuids),
        alignmentConfig: alignmentConfig,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        setProblemViewActiveErrors(previousProblemViewRef.current.errors)
        setProblemViewActiveWarnings(previousProblemViewRef.current.warnings)
        onAligned?.()
        enqueueSnackbar('Dachfläche ausgerichtet', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ausrichten der Dachfläche'), {
          variant: 'error',
        })
      },
    },
  )

  const canAlign = alignAgainstGuids.size > 0

  return (
    <CollapsibleBoxInternalState
      variant="expander"
      label="Dachfläche zuschneiden (3D only)"
      defaultExpanded={false}
      startIcon={<ContentCutIcon sx={{ color: 'grey.600', padding: '2px' }} />}
    >
      <Stack direction="column" spacing={1} width="100%">
        <Typography align="center">{alignAgainstGuids.size} Referenzflächen:</Typography>

        {alignAgainstGuids.size > 0 && (
          <Stack direction="column" spacing={1}>
            {Array.from(alignAgainstGuids).map(guid => (
              <Stack key={guid} direction="row" alignItems="center" spacing={1}>
                <Typography noWrap>{guid}</Typography>
                <IconButton
                  size="small"
                  aria-label="Referenz-Dachfläche entfernen"
                  onClick={() =>
                    setAlignAgainstGuids(prev => {
                      const next = new Set(prev)
                      next.delete(guid)
                      return next
                    })
                  }
                  sx={{ padding: 0 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
        <Button variant={'outlined'} size="small" onClick={onStartSelectingReference} fullWidth>
          Dachflächen für Zuschnitt anwählen
        </Button>

        <Stack direction="row" spacing={1} width="100%">
          <TextField
            size="small"
            type="number"
            label="Vergr. Hauptfläche"
            value={alignmentConfig.polygonExtensionSlabsToAlign}
            onChange={e =>
              setAlignmentConfig(prev => ({
                ...prev,
                polygonExtensionSlabsToAlign: Number(e.target.value) || 0,
              }))
            }
            inputProps={{ step: 0.1, min: 0 }}
            fullWidth
          />
          <TextField
            size="small"
            type="number"
            label="Vergr. Referenzflächen"
            value={alignmentConfig.polygonExtensionSlabsToAlignAgainst}
            onChange={e =>
              setAlignmentConfig(prev => ({
                ...prev,
                polygonExtensionSlabsToAlignAgainst: Number(e.target.value) || 0,
              }))
            }
            inputProps={{ step: 0.1, min: 0 }}
            fullWidth
          />
          <Button
            variant="contained"
            size="small"
            disabled={!canAlign || isLoading}
            onClick={() => submitAlignment()}
            startIcon={<ContentCutIcon />}
            fullWidth
          >
            Zuschneiden
          </Button>
        </Stack>
      </Stack>
    </CollapsibleBoxInternalState>
  )
}

export default RoofSlabAlignmentForm
